/**
 * `bar` template — a ranked horizontal bar chart. Live via `resolveGroupQuery` when `query` is
 * set, static (agent-computed) when `data` is set; `highlight_keys` paint in the accent red,
 * everything else neutral. A value axis runs under the bars (round ticks, same unit as the
 * per-row label); hovering or focusing a bar shows its exact key and value.
 */

import { useState } from "react";

import type { BarView } from "@/lib/api";
import type { Sku } from "@/lib/data";
import { resolveGroupQuery } from "@/lib/specs/evaluate";

import { niceTicks } from "./axis";
import { ChartTooltip } from "./chart-tooltip";
import { HIGHLIGHT_PRIMARY, NEUTRAL } from "./colors";
import type { ViewChrome, ViewSize } from "./view-shell";

/** The bare number. Every value on the chart is in the same unit, which the axis title names
 *  once — repeating it per row wrapped "314,461 boxes held" over three lines in the value
 *  column and made a ten-row chart twice as tall as it needed to be. */
function fmtValue(v: number): string {
  return v.toLocaleString(undefined, { maximumFractionDigits: Number.isInteger(v) ? 0 : 1 });
}

/** Number plus unit — for the tooltip, which is read on its own and has room for it. */
function fmtValueWithUnit(v: number, unit?: string | null): string {
  return unit ? `${fmtValue(v)} ${unit}` : fmtValue(v);
}

interface BarChartProps {
  spec: BarView;
  size: ViewSize;
  skus: readonly Sku[];
  chrome?: ViewChrome;
}

export function BarChart({ spec, size, skus, chrome = "full" }: BarChartProps) {
  const [hoverKey, setHoverKey] = useState<string | null>(null);
  const isLive = !!spec.query;
  const rows = isLive
    ? resolveGroupQuery(spec.query!, skus)
    : [...(spec.data ?? [])].sort((a, b) => b.value - a.value);
  const max = Math.max(1, ...rows.map((r) => r.value));

  if (rows.length === 0) {
    return <p className="text-muted-foreground text-xs">No SKUs in this cohort.</p>;
  }

  const isCard = size === "card";
  const labelClass = isCard ? "w-16 shrink-0 truncate text-xs" : "w-28 shrink-0 truncate text-sm";
  const valueClass = isCard
    ? "w-14 shrink-0 text-right text-xs tabular-nums whitespace-nowrap"
    : "w-20 shrink-0 text-right text-sm tabular-nums whitespace-nowrap";
  const ticks = niceTicks(max, isCard ? 3 : 5).filter((t) => t <= max);

  return (
    <div className="space-y-1.5">
      {!isLive && chrome !== "bare" ? (
        <p className="text-muted-foreground text-[11px] italic">computed at time of analysis</p>
      ) : null}
      <div className="space-y-1.5">
        {rows.map((r) => {
          const highlighted = spec.highlight_keys?.includes(r.key) ?? false;
          const pct = Math.max(2, (r.value / max) * 100);
          const hovered = hoverKey === r.key;
          const clear = () => setHoverKey((k) => (k === r.key ? null : k));
          return (
            <div key={r.key} className="flex items-center gap-2">
              <span
                className={labelClass}
                title={r.key}
                tabIndex={0}
                onMouseEnter={() => setHoverKey(r.key)}
                onMouseLeave={clear}
                onFocus={() => setHoverKey(r.key)}
                onBlur={clear}
              >
                {r.key}
              </span>
              <div className="bg-border/50 relative h-2.5 min-w-0 flex-1 overflow-hidden rounded">
                <div
                  className="h-full rounded"
                  style={{
                    width: `${pct}%`,
                    backgroundColor: highlighted ? HIGHLIGHT_PRIMARY : NEUTRAL,
                  }}
                  onMouseEnter={() => setHoverKey(r.key)}
                  onMouseLeave={clear}
                />
                {hovered ? (
                  <ChartTooltip x={`${pct}%`} y="0%">
                    <span className="font-semibold">{r.key}</span>:{" "}
                    {fmtValueWithUnit(r.value, spec.unit)}
                  </ChartTooltip>
                ) : null}
              </div>
              <span className={valueClass} title={fmtValueWithUnit(r.value, spec.unit)}>
                {fmtValue(r.value)}
              </span>
            </div>
          );
        })}
      </div>
      <div className="flex items-center gap-2 pt-0.5">
        <span className={labelClass} aria-hidden />
        <div className="border-border relative h-3.5 min-w-0 flex-1 border-t">
          {ticks.map((t) => (
            <span
              key={t}
              className="text-muted-foreground absolute top-1 -translate-x-1/2 text-[10px] whitespace-nowrap tabular-nums"
              style={{ left: `${(t / max) * 100}%` }}
            >
              {fmtValue(t)}
            </span>
          ))}
        </div>
        <span className={valueClass} aria-hidden />
      </div>
      {/* The value axis's title. The other axis is categorical and already labelled row by
          row, so naming the unit once here is the whole axis story. */}
      <p className="text-muted-foreground pt-2 text-center text-[10px]">
        {spec.unit ?? "value"} per {spec.query?.group_by ?? "category"}
      </p>
    </div>
  );
}
